import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import {
  InvitationAuditEvent,
  InvitationStatus,
  UserDisplayStatus,
  type UserCompleteness,
  type UserListRow,
  type UserListStats,
  type UserStatusBucket,
} from '../../api/dto/user-invitation.dto'
import { isInviteAllowedRbacRoleName } from './inviteRbacRoles'

export type DisplayStatusVariant = 'active' | 'invited' | 'expired' | 'none'

export interface DisplayStatusConfig {
  label: string
  variant: DisplayStatusVariant
  /** How the state reads as a count, where the column's bare "Yes" would not. */
  tallyLabel?: string
  /** Present only for the states whose label needs qualifying. */
  tooltip?: string
}

/**
 * Resolves the combined account status shown in the Finance Manager column. The
 * invitation lifecycle (expired, withdrawn) is folded into the status for INVITED
 * users so a single column tells the whole story.
 */
export const getDisplayStatusConfig = (
  row: Pick<UserListRow, 'displayStatus' | 'invitationStatus' | 'rbacRoles'>
): DisplayStatusConfig => {
  switch (row.displayStatus) {
    case UserDisplayStatus.Active:
      // ACTIVE only means the account exists, so it covers people who never
      // held the role and people it was taken from. Reading the role itself
      // keeps this column and the Remove access button from contradicting
      // each other.
      return (row.rbacRoles ?? []).some((role) =>
        isInviteAllowedRbacRoleName(role.name)
      )
        ? {
            label: 'Yes',
            variant: 'active',
            tallyLabel: 'Finance Managers',
            tooltip: 'Activated and holds the role',
          }
        : {
            // The account is fine, it just carries no Finance Manager role.
            // Naming the action keeps this apart from the "Yes" above, where
            // "Registered" read like a second way of saying yes.
            label: 'Needs grant',
            variant: 'none',
            tooltip: 'Account activated, does not hold the role',
          }
    case UserDisplayStatus.Invited:
      if (row.invitationStatus === InvitationStatus.Expired) {
        return { label: 'Registration expired', variant: 'expired' }
      }
      // A withdrawn link leaves no role and no way in, so the row reads as
      // never invited and can be invited again. Removing access normally takes
      // the address off every profile too, which drops the row from the list
      // altogether; this is what is left when only part of that landed.
      if (row.invitationStatus === InvitationStatus.Revoked) {
        return { label: 'Need invitation', variant: 'none' }
      }
      return { label: 'Pending registration', variant: 'invited' }
    default:
      return { label: 'Need invitation', variant: 'none' }
  }
}

/** Reading order for a tally of statuses: most access first, least last. */
const DISPLAY_STATUS_LABEL_ORDER = [
  'Finance Managers',
  'Pending registration',
  'Registration expired',
  'Needs grant',
  'Need invitation',
]

/**
 * Tallies rows by the status shown in the Finance Manager column. Every row
 * lands in exactly one bucket, so the counts add up to what was passed in.
 */
export const countByDisplayStatus = (
  rows: Parameters<typeof getDisplayStatusConfig>[0][]
): { label: string; count: number }[] => {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const { label, tallyLabel } = getDisplayStatusConfig(row)
    const key = tallyLabel ?? label
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return DISPLAY_STATUS_LABEL_ORDER.filter((label) => counts.has(label)).map(
    (label) => ({ label, count: counts.get(label) as number })
  )
}

/**
 * What the funnel on the Finance Manager column offers: the ways the role and
 * the billing companies fail to line up. There is no option for holding the
 * role with a company behind it, that is the completeness dropdown's job, so
 * the two controls never select the same rows.
 */
export const STATUS_FILTER_OPTIONS: {
  label: string
  value: UserStatusBucket
  /** Which count in UserListStats tallies the rows this option selects. */
  statKey: keyof UserListStats
}[] = [
  { label: 'Pending registration', value: 'INVITED', statKey: 'invited' },
  { label: 'Need invitation', value: 'NOT_INVITED', statKey: 'notInvited' },
  { label: 'Registration expired', value: 'EXPIRED', statKey: 'expired' },
  { label: 'Needs grant', value: 'NEEDS_GRANT', statKey: 'needsGrant' },
  { label: 'No companies', value: 'NO_COMPANIES', statKey: 'noCompanies' },
]

/** What the completeness dropdown beside Search can ask for. */
export type UserScope = 'ALL' | UserCompleteness

/**
 * The completeness dropdown's options. Incomplete is derived rather than
 * counted, so the three numbers always add up. A count is left off until the
 * first load lands, and against an API older than this client, so the label
 * reads bare rather than "(0)".
 */
export const buildScopeOptions = (
  stats: UserListStats | null
): { label: string; value: UserScope; count?: number }[] => {
  const total = stats?.total
  const complete = stats?.complete
  return [
    // Named for the people, not the filter: the control has no label of its
    // own, so a bare "All" would not say what it is all of.
    { label: 'All users', value: 'ALL', count: total },
    { label: 'Complete', value: 'COMPLETE', count: complete },
    {
      label: 'Incomplete',
      value: 'INCOMPLETE',
      count: isNil(total) || isNil(complete) ? undefined : total - complete,
    },
  ]
}

/**
 * RBAC roles that carry no permissions of their own. Everyone gets the baseline
 * "Employee" role, so showing it next to a real role adds noise and reads like a
 * grant that means something. It is only worth displaying when it is all a user
 * has.
 */
const BASELINE_RBAC_ROLE_NAMES = ['Employee']

const isBaselineRbacRoleName = (name: string): boolean =>
  BASELINE_RBAC_ROLE_NAMES.some(
    (baseline) => baseline.toLowerCase() === name.toLowerCase()
  )

/** Drops the permissionless baseline role unless it is the user's only role. */
export const getDisplayableRbacRoles = <T extends { name: string }>(
  roles: T[]
): T[] => {
  const meaningful = roles.filter((role) => !isBaselineRbacRoleName(role.name))
  return meaningful.length > 0 ? meaningful : roles
}

/** The role behind the HR Admin column. It is not grantable from this screen. */
const HR_ADMIN_RBAC_ROLE_NAME = 'HR Admin'

export const isHrAdminRbacRoleName = (name: string): boolean =>
  name.toLowerCase() === HR_ADMIN_RBAC_ROLE_NAME.toLowerCase()

/** The role behind the EE column. */
const EMPLOYEE_RBAC_ROLE_NAME = 'Employee'

export const isEmployeeRbacRoleName = (name: string): boolean =>
  name.toLowerCase() === EMPLOYEE_RBAC_ROLE_NAME.toLowerCase()

export type RoleAccessTone = 'granted' | 'pending' | 'implied' | 'none'

export interface RoleAccessConfig {
  label: string
  tone: RoleAccessTone
  /** Present only for the states whose label needs qualifying. */
  tooltip?: string
}

/**
 * Resolves one access column for a row. `matchesRole` decides which role names
 * count. A signed-up user holding no roles has the one their Cognito role
 * implies, so that reads as yes too, the same as the column's filter. Rows
 * without a login carry a default role but no access, so it never counts.
 */
export const getRoleAccessConfig = (
  row: Pick<
    UserListRow,
    'rbacRoles' | 'displayStatus' | 'impliedRbacRoleIfUnassigned'
  >,
  matchesRole: (name: string) => boolean
): RoleAccessConfig => {
  const roles = row.rbacRoles ?? []
  if (roles.length === 0) {
    const implied = row.impliedRbacRoleIfUnassigned
    return row.displayStatus === UserDisplayStatus.Active &&
      !isNil(implied) &&
      matchesRole(implied.name)
      ? { label: 'Yes', tone: 'granted' }
      : { label: 'No', tone: 'none' }
  }
  if (roles.some((role) => matchesRole(role.name))) {
    // An invited user's roles are attached to the invitation and only take
    // effect on activation, so the access is not a yes yet.
    return row.displayStatus === UserDisplayStatus.Invited
      ? {
          label: 'Pending',
          tone: 'pending',
          tooltip: 'Granted when the user activates their account',
        }
      : { label: 'Yes', tone: 'granted' }
  }
  return { label: 'No', tone: 'none' }
}

/** Formats an ISO date as a short calendar date, or a dash when missing. */
export const formatDate = (iso: string | null): string =>
  iso ? dayjs(iso).format('MMM D, YYYY') : '—'

/**
 * The numeric form the table's date columns use, so every row is the same width
 * and the columns stay narrow. The spelled-out `formatDate` is kept for prose.
 */
export const formatCellDate = (iso: string | null): string =>
  iso ? dayjs(iso).format('MM/DD/YYYY') : '—'

/** Formats an ISO date's clock time, shown as the secondary line in date cells. */
export const formatTime = (iso: string): string => dayjs(iso).format('h:mm A')

/** Formats an ISO date as date + time for the history timeline. */
export const formatDateTime = (iso: string): string =>
  dayjs(iso).format('MMM D, YYYY · h:mm A')

const AUDIT_EVENT_LABELS: Record<InvitationAuditEvent, string> = {
  [InvitationAuditEvent.UserInvited]: 'Invitation sent',
  [InvitationAuditEvent.InvitationResent]: 'Invitation resent',
  [InvitationAuditEvent.InvitationRevoked]: 'Invitation revoked',
  [InvitationAuditEvent.InvitationActivated]: 'Account activated',
  [InvitationAuditEvent.UserActivated]: 'Account activated',
  [InvitationAuditEvent.RbacRolesUpdated]: 'Roles updated',
  [InvitationAuditEvent.CompaniesSynced]: 'Company access synced',
  [InvitationAuditEvent.CompaniesRevoked]: 'Company access revoked',
}

export const formatAuditEvent = (event: InvitationAuditEvent): string =>
  AUDIT_EVENT_LABELS[event] ?? event

export const formatAuditRoleChangeDetail = (
  metadata: Record<string, unknown> | null | undefined
): string | null => {
  if (!metadata) {
    return null
  }
  const previousRoleNames = metadata.previousRoleNames
  const roleNames = metadata.roleNames
  if (!Array.isArray(previousRoleNames) || !Array.isArray(roleNames)) {
    return null
  }
  const previousLabel =
    previousRoleNames.length > 0
      ? previousRoleNames
          .filter((name): name is string => typeof name === 'string')
          .join(', ')
      : 'None'
  const nextLabel =
    roleNames.length > 0
      ? roleNames
          .filter((name): name is string => typeof name === 'string')
          .join(', ')
      : 'None'
  if (previousLabel === nextLabel) {
    return null
  }
  return `${previousLabel} → ${nextLabel}`
}

const readNameList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((name): name is string => typeof name === 'string')
    : []

/**
 * What an access-sync history entry changed, e.g. "Added Acme; removed Initech".
 * A sync moves access in both directions, so both are worth showing.
 */
export const formatAuditCompanySyncDetail = (
  metadata: Record<string, unknown> | null | undefined
): string | null => {
  if (!metadata) {
    return null
  }
  const added = readNameList(metadata.grantedClientNames)
  const removed = readNameList(metadata.revokedClientNames)
  const parts: string[] = []
  if (added.length > 0) {
    parts.push(`Added ${added.join(', ')}`)
  }
  if (removed.length > 0) {
    parts.push(`removed ${removed.join(', ')}`)
  }
  return parts.length > 0 ? parts.join('; ') : null
}

export type AuditEventColor =
  | 'blue'
  | 'gold'
  | 'red'
  | 'green'
  | 'gray'
  | 'purple'

const AUDIT_EVENT_COLORS: Record<InvitationAuditEvent, AuditEventColor> = {
  [InvitationAuditEvent.UserInvited]: 'blue',
  [InvitationAuditEvent.InvitationResent]: 'gold',
  [InvitationAuditEvent.InvitationRevoked]: 'red',
  [InvitationAuditEvent.InvitationActivated]: 'green',
  [InvitationAuditEvent.UserActivated]: 'green',
  [InvitationAuditEvent.RbacRolesUpdated]: 'purple',
  [InvitationAuditEvent.CompaniesSynced]: 'purple',
  [InvitationAuditEvent.CompaniesRevoked]: 'red',
}

export const getAuditEventColor = (
  event: InvitationAuditEvent
): AuditEventColor => AUDIT_EVENT_COLORS[event] ?? 'gray'

/** "1 user" / "2 users". Regular nouns only, which is all this screen uses. */
export const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

/** "A", "A and B", "A, B and C", "A, B and 3 more". */
export const joinNames = (names: string[]): string => {
  if (names.length <= 2) return names.join(' and ')
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`
}
